import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Team } from '../models/team';
import { environment } from '../../environments/environment';
import { ITeamOpenPositions } from '../_interfaces/teamOpenPositions';
import { ITeamStatus } from '../_interfaces/teamStatus';
import { ITeamRecommendations } from '../_interfaces/teamRecommendations';

@Injectable({
  providedIn: 'root',
})
export class TeamsService {
  baseUrl: string = environment.apiUrl;

  constructor(private HttpClient: HttpClient) {}


  /**
   * Retrieves all teams from the server.
   *
   * @returns An observable that emits an array of Team objects
   */
  getTeams(): Observable<Team[]> {
    return this.HttpClient.get<Team[]>(
      this.baseUrl + '/teams'
    );
  }

  getTeam(teamId: number): Observable<Team> {
    return this.HttpClient.get<Team>(
      this.baseUrl + '/teams/'+teamId
    );
  }

  /**
   * Deletes a team from the server.
   *
   * @param id The ID of the team to delete
   * @returns An observable that emits a void value when the team is successfully deleted
   */
  deleteTeam(id: number): Observable<void> {
    return this.HttpClient.delete<void>(
      this.baseUrl + '/teams/' + id
    );
  }


  /**
   * Adds a team to the server.
   *
   * @param team The Team object to add
   * @returns An observable that emits the newly added Team object
   */
  addTeam(team: Team): Observable<Team> {
    return this.HttpClient.post<Team>(
      this.baseUrl + '/teams',
      team
    );
  }


  /**
   * Updates a team on the server.
   *
   * @param team The Team object with updated values
   * @returns An observable that emits the updated Team object
   */
  updateTeam(team: Team): Observable<Team> {
    return this.HttpClient.put<Team>(
      this.baseUrl + '/teams/' + team.id,
      team
    );
  }

  /**
   * Retrieves open positions (roles) from the server, grouped by team.
   *
   * @returns Observable<TeamOpenPositions[]> A list of teams with their open positions.
   */
  getOpenPositionsGroupedByTeam(): Observable<ITeamOpenPositions[]> {
    let params = new HttpParams();
    params = params.append('status', 'open');
    params = params.append('group_by', 'team');

    return this.HttpClient.get<ITeamOpenPositions[]>(`${this.baseUrl}/teams/positions`, { params });
  }

  /**
   * Retrieves the staffing status for all teams.
   * @returns An Observable that emits an array of ITeamStatus objects.
   */
  getTeamsStatus(): Observable<ITeamStatus[]> {
    return this.HttpClient.get<ITeamStatus[]>(`${this.baseUrl}/teams/status`);
  }

  /**
   * Retrieves recommendations for a team.
   * @param teamId The ID of the team
   * @returns An observable with the recommendations
   */
  getTeamRecommendations(teamId: number): Observable<ITeamRecommendations> {
    return this.HttpClient.get<ITeamRecommendations>(
      this.baseUrl + '/teams/' + teamId + '/recommendations'
    );
  }

}
